import { last } from 'lodash';
import { nanoid } from 'nanoid';
import type { Poll } from 'schema/poll';
import { ICState, TrackingState as ICTrackingState, TrackingState } from 'schema/state';
import { TrackActionParams } from '../actions';
import { createDefaultTrackingState } from '../actions/creator';
import { selectDisplayedOrAllGroupQuestions, selectQuestion } from '../selects';
import { AnswerTrackerParams, EventIdentity, ITracking } from '../trackingTypes';
import {
  calculateCorrectAnswerCount,
  calculatePoints,
  findLastInStack,
  getMapFromStack,
  getOverallResult,
  isMediaFileQuestion,
  questionHasTextField,
} from '../utils';
import { sha256 } from './hashing';

interface TrackReturn {
  currentHash: string | null;
  trackPromise: Promise<void>;
}

async function trackAnswer({
  state,
  poll,
  questionId,
  tracking,
  onTrackStatusUpdate,
  previousHash,
}: {
  state: ICState;
  questionId: string;
  poll: Poll;
  tracking: ITracking;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
  previousHash: string | null;
}): Promise<TrackReturn> {
  const question = selectQuestion(poll, questionId);
  if (isMediaFileQuestion(question)) {
    return Promise.resolve({ currentHash: previousHash, trackPromise: Promise.resolve() });
  }

  const answerState = findLastInStack(state.answersStack, questionId);

  const hasTextField = questionHasTextField(question, answerState);
  const answeredOthers = hasTextField ? answerState.answeredOthers : undefined;
  const { trackingId, answersIds, answersOrder, answeredIds, awardAmount, elapsedTime, isTimeExpired } = answerState;

  const parameters = {
    questionId,
    answersIds,
    answersOrder,
    answeredIds,
    answeredOthers,
    elapsedTime,
    awardAmount,
    isTimeExpired: isTimeExpired === 'changedSinceExpired',
    previousHash,
  } satisfies AnswerTrackerParams;

  const selectedParameters = [
    tracking.commonData.quiz_id,
    tracking.commonData.polls_ic,
    parameters.questionId,
    parameters.answeredIds ?? null,
    parameters.answeredOthers ?? null,
    parameters.previousHash ?? null,
  ];
  const currentHash = await sha256(JSON.stringify(selectedParameters));

  let retryIds: string[] | undefined;

  const existingTrackingState = state.trackingStack.find(({ id }) => id === trackingId);
  let trackingState: TrackingState;

  /** При повторной отправке пикселя, отправляются только не отправленный события */
  if (existingTrackingState) {
    retryIds = existingTrackingState.tracking.filter(it => it.status === 'rejected').map(it => it.id);
    trackingState = existingTrackingState;
  } else {
    const createdTrackingState = createDefaultTrackingState('answer', trackingId);
    onTrackStatusUpdate({ tracking: [], trackingState: createdTrackingState });
    trackingState = createdTrackingState;
  }

  const { sendedEvents, systemPromises, customPromises } = await tracking.answer(
    { ...parameters, currentHash },
    retryIds,
  );

  onTrackStatusUpdate({
    trackingState,
    tracking: sendedEvents.map(event => ({ id: event.id, isSystem: event.isSystem, status: 'pending' })),
  });

  const trackPromise = new Promise<void>((resolve, reject) => {
    void systemPromises.then(result => {
      setTrackingState(result, trackingState, { isSystem: true }, onTrackStatusUpdate);
      const isAllSystemSent = result.every(it => it.status === 'fulfilled');
      if (isAllSystemSent) resolve();
      else reject(result);
    });
    void customPromises.then(result => {
      setTrackingState(result, trackingState, { isSystem: false }, onTrackStatusUpdate);
    });
  });

  return { currentHash, trackPromise };
}

async function onSendFinalTracking({
  poll,
  tracking,
  nextState,
  state,
  onTrackStatusUpdate,
  previousHash,
}: {
  poll: Poll;
  nextState: ICState;
  state: ICState;
  tracking: ITracking;
  previousHash: string | null;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
}): Promise<TrackReturn> {
  /** Если следующий экран финальный, отправляем пиксель final вместе с ответом на последний вопрос */
  if (nextState.finalId) {
    trackOverall({ state, nextState, poll, tracking, onTrackStatusUpdate });
    return trackFinished({ previousHash, state, nextState, poll, tracking, onTrackStatusUpdate });
  }

  return { currentHash: previousHash, trackPromise: Promise.resolve() };
}

const setTrackingState = (
  result: PromiseSettledResult<EventIdentity>[],
  trackingState: ICTrackingState,
  {
    isSystem = false,
  }: {
    isSystem?: boolean;
  },
  onTrackStatusUpdate: (v: TrackActionParams) => void,
) => {
  const tracking = result.map(it => {
    if (it.status === 'fulfilled') return { id: it.value.id, isSystem, status: it.status };
    return { id: getIdFromReason(it), isSystem, status: it.status };
  });
  onTrackStatusUpdate({ trackingState, tracking });
};

async function trackFinished({
  state,
  nextState,
  poll,
  tracking,
  onTrackStatusUpdate,
  previousHash,
}: {
  state: ICState;
  nextState: ICState;
  poll: Poll;
  tracking: ITracking;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
  previousHash: string | null;
}): Promise<TrackReturn> {
  if (tracking.isFinished) {
    return Promise.resolve({ currentHash: previousHash, trackPromise: Promise.resolve() });
  }

  const answersMap = getMapFromStack(nextState.answersStack);

  const { finalId } = nextState;
  const points = calculatePoints(nextState.answersStack);

  if (!finalId) {
    throw new Error('Tried to send finish while not on finish!');
  }

  const correctAnswersCount = calculateCorrectAnswerCount(answersMap, poll.questions);

  /** При повторной отправке пикселя, отправляются только не отправленный события */
  const tracks = state.trackingStack.find(it => it.type === 'finish')?.tracking;
  const retryIds = tracks != null ? tracks.filter(it => it.status === 'rejected').map(it => it.id) : undefined;

  const parameters = {
    finalId,
    points,
    correctAnswersCount,
    previousHash,
    isTimeExpired: last(state.answersStack)?.isTimeExpired === 'changedSinceExpired',
  };

  const selectedParameters = [
    tracking.commonData.quiz_id,
    tracking.commonData.polls_ic,
    parameters.finalId,
    previousHash ?? null,
  ];

  const currentHash = await sha256(JSON.stringify(selectedParameters));
  const { sendedEvents, systemPromises, customPromises } = tracking.finish({ ...parameters, currentHash }, retryIds);

  /** Получение/Создание trackingState из state */
  const found = nextState.trackingStack.find(({ type }) => type === 'finish');
  const trackingState = found ?? createDefaultTrackingState('finish', nanoid(10));
  if (!found) {
    onTrackStatusUpdate({ trackingState, tracking: [] });
  }

  onTrackStatusUpdate({
    trackingState,
    tracking: sendedEvents.map(event => ({ id: event.id, isSystem: event.isSystem, status: 'pending' })),
  });

  void customPromises.then(result => {
    setTrackingState(result, trackingState, { isSystem: false }, onTrackStatusUpdate);
  });

  const trackPromise = new Promise<void>((resolve, reject) => {
    void systemPromises.then(result => {
      setTrackingState(result, trackingState, { isSystem: true }, onTrackStatusUpdate);
      const isAllSystemSent = result.every(it => it.status === 'fulfilled');
      if (isAllSystemSent) resolve();
      else reject(result);
    });
  }).catch(() => {
    tracking.isFinished = false;
    return Promise.reject();
  });

  return { currentHash, trackPromise };
}

function trackOverall({
  state,
  nextState,
  poll,
  tracking,
  onTrackStatusUpdate,
}: {
  state: ICState;
  nextState: ICState;
  poll: Poll;
  tracking: ITracking;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
}) {
  const { finalId } = nextState;

  if (!finalId) {
    throw new Error('Tried to send overall while not on finish!');
  }

  const { sendedEvents, customPromises } = tracking.overall(getOverallResult({ state, poll, finalId }));

  const found = nextState.trackingStack.find(({ type }) => type === 'overall');
  const trackingState = found ?? createDefaultTrackingState('overall', nanoid(10));

  if (!found) {
    onTrackStatusUpdate({ trackingState, tracking: [] });
  }

  onTrackStatusUpdate({
    trackingState,
    tracking: sendedEvents.map(event => ({ id: event.id, isSystem: event.isSystem, status: 'pending' })),
  });

  void customPromises.then(result => {
    setTrackingState(result, trackingState, { isSystem: false }, onTrackStatusUpdate);
  });
}

export async function onRetry({
  state,
  poll,
  error,
  tracking,
  nextState,
  onTrackStatusUpdate,
}: {
  poll: Poll;
  state: ICState;
  nextState: ICState;
  tracking: ITracking;
  error?: TrackingError;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
}): Promise<string | null> {
  const questionId = error?.questionId;
  const groupId = error?.groupId;
  const previousHash = error?.previousHash ?? null;

  const answersMap = getMapFromStack(state.answersStack);

  const questionIdsToTrack: string[] = [];

  if (groupId) {
    const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, groupId, state.answersStack);
    const groupQuestionWithErrors = groupQuestions.filter(({ id }) => {
      const trackingId = answersMap[id]?.trackingId;
      if (!trackingId) return false;

      const tracking = findLastInStack(state.trackingStack, trackingId).tracking;
      return tracking.some(it => it.status === 'rejected');
    });
    questionIdsToTrack.push(...groupQuestionWithErrors.map(({ id }) => id));
  } else if (questionId) {
    questionIdsToTrack.push(questionId);
  }

  return trackAnswerAndFinal({
    poll,
    state,
    nextState,
    tracking,
    questionIdsToTrack,
    onTrackStatusUpdate,
    previousHash,
  });
}

export async function onTrack({
  state,
  poll,
  tracking,
  nextState,
  onTrackStatusUpdate,
  previousHash,
}: {
  poll: Poll;
  state: ICState;
  nextState: ICState;
  tracking: ITracking;
  onTrackStatusUpdate: (v: TrackActionParams) => void;
  previousHash: string | null;
}) {
  const questionIdsToTrack: string[] = [];

  if (state.groupId) {
    const group = poll.groups.find(group => group.id === state.groupId);
    const groupQuestions = selectDisplayedOrAllGroupQuestions(poll, state.groupId, state.answersStack);
    const groupQuestionsToTrack = group?.data.hasSequentialDisplay
      ? groupQuestions.slice(groupQuestions.length - 1)
      : groupQuestions;
    questionIdsToTrack.push(...groupQuestionsToTrack.map(({ id }) => id));
  } else if (state.questionId) {
    questionIdsToTrack.push(state.questionId);
  }

  return trackAnswerAndFinal({
    poll,
    state,
    nextState,
    tracking,
    questionIdsToTrack,
    onTrackStatusUpdate,
    previousHash,
  });
}

async function trackAnswerAndFinal({
  poll,
  state,
  nextState,
  tracking,
  questionIdsToTrack,
  onTrackStatusUpdate,
  previousHash,
}: {
  poll: Poll;
  state: ICState;
  nextState: ICState;
  tracking: ITracking;
  questionIdsToTrack: string[];
  onTrackStatusUpdate: (v: TrackActionParams) => void;
  previousHash: string | null;
}) {
  let currentHash: string | null = previousHash;

  async function beginSendingAnswers() {
    const trackAnswerPromises: Promise<unknown>[] = [];

    for (const questionId of questionIdsToTrack) {
      const { currentHash: nextHash, trackPromise } = await trackAnswer({
        poll,
        state,
        tracking,
        questionId,
        previousHash: currentHash,
        onTrackStatusUpdate,
      });
      currentHash = nextHash;
      trackAnswerPromises.push(trackPromise);
    }

    return { trackAnswerPromises };
  }

  async function beginSendingFinal() {
    const { currentHash: nextHash, trackPromise } = await onSendFinalTracking({
      previousHash: currentHash,
      onTrackStatusUpdate,
      nextState,
      state,
      tracking,
      poll,
    });
    currentHash = nextHash;
    return { trackPromise };
  }

  const { trackAnswerPromises } = await beginSendingAnswers();

  if (poll.useStrict) {
    try {
      const trackAnswersResult = await Promise.allSettled(trackAnswerPromises);
      const isSomeAnswerTrackingFailed = trackAnswersResult.some(track => track.status === 'rejected');
      if (isSomeAnswerTrackingFailed) {
        throw new TrackingError(state.questionId, state.groupId, currentHash);
      }
      const { trackPromise: trackFinalPromise } = await beginSendingFinal();
      await trackFinalPromise;
    } catch {
      throw new TrackingError(state.questionId, state.groupId, currentHash);
    }
  } else {
    const { trackPromise: trackFinalPromise } = await beginSendingFinal();
    void Promise.all([...trackAnswerPromises, trackFinalPromise]).catch(console.error);
  }

  return currentHash;
}

export class TrackingError extends Error {
  constructor(
    public readonly questionId: string | null,
    public readonly groupId: string | null,
    public readonly previousHash: string | null,
  ) {
    super(`Track question ${questionId} error`);
    this.questionId = questionId;
    this.groupId = groupId;
  }
}

function getIdFromReason({ reason }: { reason: unknown }): string {
  return !!reason && typeof reason === 'object' && 'id' in reason && typeof reason.id === 'string'
    ? reason.id
    : '<unknown>';
}
